import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { EditIcon, DeleteIcon, AddIcon } from '../components/Icons';
import './AdminPromotions.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminPromotions() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // all, active, inactive
  });
  const [editingRule, setEditingRule] = useState(null);
  const [editForm, setEditForm] = useState({
    name_lt: '',
    name_en: '',
    name_de: '',
    description_lt: '',
    description_en: '',
    description_de: '',
    rule_type: 'percentage_discount',
    rule_config: {},
    applicable_to: 'all',
    applicable_ids: [],
    priority: 0,
    is_active: true,
    valid_from: '',
    valid_until: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: promotionsData, isLoading } = useQuery({
    queryKey: ['admin-promotions', page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active' ? '1' : '0');

      const res = await apiClient.get(`/admin/promotions?${params.toString()}`);
      return res.data;
    },
  });

  const rules = promotionsData?.rules || [];

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/promotions', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-promotions']);
      setShowCreateModal(false);
      setEditingRule(null);
      success('Akcijų taisyklė sėkmingai sukurta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant akcijų taisyklę');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/promotions/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-promotions']);
      setShowCreateModal(false);
      setEditingRule(null);
      notification.success('Akcijų taisyklė sėkmingai atnaujinta');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant akcijų taisyklę');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/promotions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-promotions']);
      success('Akcijų taisyklė pašalinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant akcijų taisyklę');
    },
  });

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setEditForm({
      name_lt: rule.name_lt ?? rule.name ?? '',
      name_en: rule.name_en ?? '',
      name_de: rule.name_de ?? '',
      description_lt: rule.description_lt ?? rule.description ?? '',
      description_en: rule.description_en ?? '',
      description_de: rule.description_de ?? '',
      rule_type: rule.rule_type || 'percentage_discount',
      rule_config: rule.rule_config || {},
      applicable_to: rule.applicable_to || 'all',
      applicable_ids: rule.applicable_ids || [],
      priority: rule.priority || 0,
      is_active: rule.is_active !== undefined ? rule.is_active : true,
      valid_from: rule.valid_from ? rule.valid_from.split('T')[0] + 'T' + rule.valid_from.split('T')[1]?.substring(0, 5) : '',
      valid_until: rule.valid_until ? rule.valid_until.split('T')[0] + 'T' + rule.valid_until.split('T')[1]?.substring(0, 5) : ''
    });
    setShowCreateModal(true);
  };

  const handleNew = () => {
    setEditingRule(null);
    setEditForm({
      name_lt: '',
      name_en: '',
      name_de: '',
      description_lt: '',
      description_en: '',
      description_de: '',
      rule_type: 'percentage_discount',
      rule_config: {},
      applicable_to: 'all',
      applicable_ids: [],
      priority: 0,
      is_active: true,
      valid_from: '',
      valid_until: ''
    });
    setShowCreateModal(true);
  };

  const handleSave = () => {
    const hasName = (editForm.name_lt || '').trim() || (editForm.name_en || '').trim() || (editForm.name_de || '').trim();
    if (!hasName || !editForm.rule_type) {
      notification.info('Užpildykite pavadinimą bent viena kalba ir taisyklės tipą');
      return;
    }

    const dataToSend = {
      ...editForm,
      rule_config: editForm.rule_config,
      applicable_ids: editForm.applicable_ids.length > 0 ? editForm.applicable_ids : null,
      valid_from: editForm.valid_from || null,
      valid_until: editForm.valid_until || null
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT');
  };

  if (isLoading) {
    return <div className="admin-promotions-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-promotions-page container">
      <div className="promotions-header">
        <h1>Akcijų taisyklės</h1>
        <button onClick={handleNew} className="btn btn-primary btn-sm">
          <AddIcon />
          Pridėti taisyklę
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal pavadinimą arba aprašymą..."
            />
          </div>
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
              className="input"
            >
              <option value="all">Visi</option>
              <option value="active">Aktyvūs</option>
              <option value="inactive">Neaktyvūs</option>
            </select>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '', status: 'all' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      <div className="promotions-list">
        {rules && rules.length > 0 ? (
          <>
            {rules.map((rule) => (
            <div key={rule.id} className="promotion-card">
              <div className="promotion-info">
                <h3>{rule.name}</h3>
                {rule.description && <p className="promotion-description">{rule.description}</p>}
                <div className="promotion-details">
                  <div className="detail-item">
                    <span className="label">Tipas:</span>
                    <span className="value">
                      {rule.rule_type === 'percentage_discount' && 'Procentinė nuolaida'}
                      {rule.rule_type === 'fixed_discount' && 'Fiksuota nuolaida'}
                      {rule.rule_type === 'buy_x_get_y' && '2 už 1'}
                      {rule.rule_type === 'gift' && 'Dovana'}
                      {rule.rule_type === 'free_shipping' && 'Nemokamas pristatymas'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Prioritetas:</span>
                    <span className="value">{rule.priority}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Galioja iki:</span>
                    <span className="value">{formatDate(rule.valid_until)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Statusas:</span>
                    <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
                      {rule.is_active ? 'Aktyvus' : 'Neaktyvus'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="promotion-actions">
                <div className="action-buttons">
                  <button
                    onClick={() => handleEdit(rule)}
                    className="btn-icon btn-icon-secondary"
                    title="Redaguoti"
                  >
                    <EditIcon />
                  </button>
                  <button
                    onClick={() => {
                      setConfirmModal({
                        isOpen: true,
                        title: 'Pašalinti akcijų taisyklę',
                        message: `Ar tikrai norite pašalinti taisyklę "${rule.name}"?`,
                        type: 'danger',
                        confirmText: 'Pašalinti',
                        onConfirm: () => {
                          deleteMutation.mutate(rule.id);
                          setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
                        },
                        onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
                      });
                    }}
                    className="btn-icon btn-icon-danger"
                    title="Pašalinti"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            </div>
            ))}
            {promotionsData?.pagination && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Pagination
                  page={promotionsData.pagination.page}
                  totalPages={promotionsData.pagination.pages}
                  totalItems={promotionsData.pagination.total}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>Akcijų taisyklių nėra. Sukurkite pirmąją taisyklę.</p>
          </div>
        )}
      </div>

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <PromotionModal
          editForm={editForm}
          setEditForm={setEditForm}
          editingRule={editingRule}
          onSave={handleSave}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRule(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

// Akcijų taisyklės modalas
function PromotionModal({ editForm, setEditForm, editingRule, onSave, onClose, isLoading }) {
  const [activeLang, setActiveLang] = useState('lt');
  const updateRuleConfig = (key, value) => {
    setEditForm(prev => ({
      ...prev,
      rule_config: {
        ...prev.rule_config,
        [key]: value
      }
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{editingRule ? 'Redaguoti akcijų taisyklę' : 'Nauja akcijų taisyklė'}</h2>

        <p className="admin-lang-hint">Pavadinimas ir aprašymas – įveskite bent viena kalba (LT / EN / DE).</p>
        <div className="admin-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
              onClick={() => setActiveLang(code)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label}) *</label>
          <input
            type="text"
            value={editForm[`name_${activeLang}`] ?? ''}
            onChange={(e) => setEditForm({ ...editForm, [`name_${activeLang}`]: e.target.value })}
            className="input"
            placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Name' : 'Bezeichnung'}
          />
        </div>

        <div className="form-group">
          <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
          <textarea
            value={editForm[`description_${activeLang}`] ?? ''}
            onChange={(e) => setEditForm({ ...editForm, [`description_${activeLang}`]: e.target.value })}
            className="input"
            rows="3"
            placeholder={activeLang === 'lt' ? 'Aprašymas' : activeLang === 'en' ? 'Description' : 'Beschreibung'}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Taisyklės tipas *</label>
            <select
              value={editForm.rule_type}
              onChange={(e) => setEditForm({ ...editForm, rule_type: e.target.value, rule_config: {} })}
              className="input"
            >
              <option value="percentage_discount">Procentinė nuolaida</option>
              <option value="fixed_discount">Fiksuota nuolaida</option>
              <option value="buy_x_get_y">2 už 1 (Buy X Get Y)</option>
              <option value="gift">Dovana</option>
              <option value="free_shipping">Nemokamas pristatymas nuo X</option>
            </select>
          </div>

          <div className="form-group">
            <label>Prioritetas</label>
            <input
              type="number"
              value={editForm.priority}
              onChange={(e) => setEditForm({ ...editForm, priority: parseInt(e.target.value) || 0 })}
              className="input"
            />
          </div>
        </div>

        {/* Taisyklės konfigūracija pagal tipą */}
        {editForm.rule_type === 'percentage_discount' && (
          <div className="form-group">
            <label>Nuolaida (%) *</label>
            <input
              type="number"
              step="1"
              value={editForm.rule_config.percentage || ''}
              onChange={(e) => updateRuleConfig('percentage', parseFloat(e.target.value) || 0)}
              className="input"
              required
            />
          </div>
        )}

        {editForm.rule_type === 'fixed_discount' && (
          <div className="form-group">
            <label>Nuolaida (€) *</label>
            <input
              type="number"
              step="0.01"
              value={editForm.rule_config.amount || ''}
              onChange={(e) => updateRuleConfig('amount', parseFloat(e.target.value) || 0)}
              className="input"
              required
            />
          </div>
        )}

        {editForm.rule_type === 'buy_x_get_y' && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Pirkite (X) *</label>
                <input
                  type="number"
                  value={editForm.rule_config.buy_quantity || ''}
                  onChange={(e) => updateRuleConfig('buy_quantity', parseInt(e.target.value) || 0)}
                  className="input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Gaukite (Y) *</label>
                <input
                  type="number"
                  value={editForm.rule_config.get_quantity || ''}
                  onChange={(e) => updateRuleConfig('get_quantity', parseInt(e.target.value) || 0)}
                  className="input"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Gaukite nuolaidą (%)</label>
              <input
                type="number"
                step="1"
                value={editForm.rule_config.discount_percentage || ''}
                onChange={(e) => updateRuleConfig('discount_percentage', parseFloat(e.target.value) || 0)}
                className="input"
                placeholder="100 = nemokamai"
              />
            </div>
          </>
        )}

        {editForm.rule_type === 'free_shipping' && (
          <div className="form-group">
            <label>Nemokamas pristatymas nuo (€) *</label>
            <input
              type="number"
              step="0.01"
              value={editForm.rule_config.min_order_value || ''}
              onChange={(e) => updateRuleConfig('min_order_value', parseFloat(e.target.value) || 0)}
              className="input"
              required
            />
          </div>
        )}

        {editForm.rule_type === 'gift' && (
          <div className="form-group">
            <label>Dovanos produkto ID *</label>
            <input
              type="number"
              value={editForm.rule_config.gift_product_id || ''}
              onChange={(e) => updateRuleConfig('gift_product_id', parseInt(e.target.value) || null)}
              className="input"
              required
            />
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Galioja nuo</label>
            <input
              type="datetime-local"
              value={editForm.valid_from}
              onChange={(e) => setEditForm({ ...editForm, valid_from: e.target.value })}
              className="input"
            />
          </div>

          <div className="form-group">
            <label>Galioja iki</label>
            <input
              type="datetime-local"
              value={editForm.valid_until}
              onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={editForm.is_active}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
            />
            <span>Aktyvus</span>
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Atšaukti
          </button>
          <button
            onClick={onSave}
            disabled={isLoading || !((editForm.name_lt || '').trim() || (editForm.name_en || '').trim() || (editForm.name_de || '').trim())}
            className="btn btn-primary"
          >
            {isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminPromotions;
